import { forwardRef, useImperativeHandle, useRef } from 'react';
import useModalState from '@sdk/components/ui/modal/useModalState';
import Modal from '@sdk/components/ui/modal/Modal';
import H3 from '@sdk/components/ui/typography/H3';
import Button from '@sdk/components/ui/buttons/Button';
import ProgressButton from '@sdk/components/ui/buttons/ProgressButton';
import gettext from '@sdk/lang/gettext';
import BrandLoader from '@sdk/components/ui/BrandLoader';
import ModalErrorDisplay from '@sdk/components/ui/modal/components/modalErrorDisplay/ModalErrorDisplay';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';
import type { SaveLaborCostSettings } from '@sdk/labor/types';
import type { PersonasDict } from '@sdk/personaCollection/types/PersonasDict';
import type { OrgSettings } from '@sdk/authentication/types/OrganisationType';
import { INPUT_HEIGHT } from '@sdk/components/forms/constants';

import { useFormValidation } from '@web/common/hooks/useFormValidation';

import type { UpdateOrgSettings } from '../onboardingChecklist.types';
import {
  Container,
  CenterContentRow,
  ModalContent,
  FooterRow,
  ButtonContainer,
} from '../components/modalBase.style';

import Clopening from './Clopening/Clopening';
import FirstDayOfWeek from './FirstDayOfWeek';
import WeeklyOvertime from './WeeklyOvertime';
import VisibilitySchedules from './VisibilitySchedules/VisibilitySchedules';
import { useSetScheduleModal } from './setScheduleModal.hooks';
import { LoaderContainer } from './setScheduleModal.styles';

type ForwardedAttrs = {
  open: () => void;
};

type Props = {
  orgSettings: OrgSettings;
  saveLaborCostSettingsInAngular: SaveLaborCostSettings;
  personasDict: PersonasDict;
  orgLocationsCount: number;
  updateOrgSettings: UpdateOrgSettings;
};

const SetScheduleModal = forwardRef<ForwardedAttrs, Props>(
  (
    {
      orgSettings,
      saveLaborCostSettingsInAngular,
      personasDict,
      orgLocationsCount,
      updateOrgSettings,
    },
    ref
  ) => {
    const { isVisible, open, close } = useModalState();
    const config = useRef({ noDismiss: true }).current;
    const {
      laborCostFields,
      laborCostReset,
      orgSettingsFields,
      orgSettingsReset,
      onSubmitHandle,
      isLoaded,
      submitErrors,
    } = useSetScheduleModal({
      orgSettings,
      saveLaborCostSettingsInAngular,
      close,
      personasDict,
      updateOrgSettings,
      isVisible,
    });
    const { isValid: orgSettingsFormValid } = useFormValidation(orgSettingsFields);
    const { isValid: laborCostFormValid } = useFormValidation(laborCostFields);

    useImperativeHandle(ref, () => ({
      open,
    }));

    if (!isVisible) {
      return null;
    }

    return (
      <Modal fixed config={config} isVisible={isVisible} onDismiss={close}>
        <Container>
          {isLoaded ? (
            <LoaderContainer>
              <BrandLoader />
            </LoaderContainer>
          ) : (
            <>
              <CenterContentRow pTop={20} pBottom={20}>
                <H3>{gettext('Set schedule rules')}</H3>
              </CenterContentRow>

              <ModalContent pLeft={15} pRight={15}>
                <FirstDayOfWeek firstDayOfWeekField={orgSettingsFields.firstDayOfWeek} />

                <VisibilitySchedules
                  orgSettings={orgSettings}
                  orgSettingsFields={orgSettingsFields}
                  orgLocationsCount={orgLocationsCount}
                />

                <WeeklyOvertime
                  overtimeEnabledField={laborCostFields.overtimeEnabled}
                  overtimeWeekThresholdField={laborCostFields.overtimeWeekThreshold}
                  overtimeWeekMultiplierField={laborCostFields.overtimeWeekMultiplier}
                />

                <Clopening
                  restBreakEnabledField={orgSettingsFields.restBreakEnabled}
                  restBreakThresholdField={orgSettingsFields.restBreakThreshold}
                />
              </ModalContent>

              <FooterRow mTop={20}>
                <ButtonContainer pRight={10}>
                  <Button
                    buttonType={ButtonType.default}
                    onClick={() => {
                      orgSettingsReset();
                      laborCostReset();
                      close();
                    }}>
                    {gettext('Cancel')}
                  </Button>
                </ButtonContainer>

                <ButtonContainer>
                  <ProgressButton
                    buttonType={ButtonType.primary}
                    onClick={(): Promise<void> => onSubmitHandle()}
                    height={INPUT_HEIGHT}
                    disabled={!orgSettingsFormValid || !laborCostFormValid}>
                    {gettext('Save')}
                  </ProgressButton>
                </ButtonContainer>
              </FooterRow>
            </>
          )}
        </Container>
        <ModalErrorDisplay error={submitErrors?.length ? submitErrors[0].message : ''} />
      </Modal>
    );
  }
);
SetScheduleModal.displayName = 'SetScheduleModal';

export default SetScheduleModal;
